import React, { useMemo, useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Package, Clock } from "lucide-react";
import { formatINR } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Fast-entry searchable product picker (Tally / FMCG billing style).
 * Props:
 *  - products:        product master array
 *  - value:           selected product_id
 *  - onSelect:        (product) => void
 *  - rateKey:         "distributor_price" | "super_stockist_price"
 *  - testId:          data-testid prefix
 *  - autoOpen:        if true, popover opens automatically on mount (focus goes to search)
 *  - recentSkus:      ordered array of recently-used SKUs (uppercase) — shown first when query is empty
 */
function scoreProduct(p, q) {
  if (!q) return 0;
  const sku = (p.sku || "").toLowerCase();
  const name = (p.name || "").toLowerCase();
  const cat = (p.category || "").toLowerCase();
  // 1. Exact SKU match → 1000
  if (sku === q) return 1000;
  // 2. Starts-with SKU → 800, starts-with name → 700
  if (sku.startsWith(q)) return 800;
  if (name.startsWith(q)) return 700;
  // 3. Word-boundary starts-with in name (e.g. "det" matches "XTRA Detergent…")
  if (new RegExp("\\b" + q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).test(name)) return 600;
  // 4. Contains in SKU → 400, contains in name → 300
  if (sku.includes(q)) return 400;
  if (name.includes(q)) return 300;
  // 5. Contains in category → 100
  if (cat.includes(q)) return 100;
  return -1;
}

export default function ProductPicker({
  products = [], value, onSelect, rateKey = "distributor_price",
  testId = "pfi-product", autoOpen = false, recentSkus = [],
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = useMemo(() => products.find((p) => p.id === value) || null, [products, value]);

  useEffect(() => { if (autoOpen && !value) setOpen(true); }, [autoOpen, value]);
  useEffect(() => { if (open) setQuery(""); }, [open]);

  const ranked = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      // Empty state: recent first, then the rest (top 20)
      if (recentSkus.length) {
        const recentSet = new Set(recentSkus.map((s) => s.toUpperCase()));
        const recents = recentSkus
          .map((sku) => products.find((p) => (p.sku || "").toUpperCase() === sku.toUpperCase()))
          .filter(Boolean);
        const others = products.filter((p) => !recentSet.has((p.sku || "").toUpperCase())).slice(0, 20 - recents.length);
        return { rows: [...recents.slice(0, 20), ...others], showRecent: recents.length };
      }
      return { rows: products.slice(0, 20), showRecent: 0 };
    }
    const scored = products.map((p) => ({ p, s: scoreProduct(p, q) })).filter((x) => x.s > 0);
    scored.sort((a, b) => b.s - a.s);
    return { rows: scored.slice(0, 60).map((x) => x.p), showRecent: 0 };
  }, [products, query, recentSkus]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full h-8 justify-between font-normal px-2.5 text-[12.5px] bg-white hover:bg-slate-50 border-slate-200"
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              <span className="text-slate-900 font-medium truncate">{selected.name || selected.sku}</span>
              <span className="mono text-[10.5px] text-slate-400 shrink-0">{selected.sku}</span>
            </span>
          ) : (
            <span className="text-slate-400">Search product by name or SKU…</span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[min(680px,95vw)] max-h-[60vh]" align="start" sideOffset={4}>
        <Command shouldFilter={false} className="max-h-[60vh]">
          <CommandInput
            placeholder="Search by SKU or name · ↑↓ to move · Enter to select"
            value={query} onValueChange={setQuery}
            data-testid={`${testId}-search`}
          />
          <CommandList className="max-h-[50vh] overflow-y-auto">
            <CommandEmpty>
              <div className="py-8 text-center text-slate-500 text-sm flex flex-col items-center gap-2">
                <Package className="h-5 w-5 text-slate-300" />
                No products match &quot;{query}&quot;
              </div>
            </CommandEmpty>
            {ranked.rows.length > 0 && (
              <CommandGroup heading={!query ? (ranked.showRecent ? "Recent · Quick Pick" : "Quick Pick") : `${ranked.rows.length} result${ranked.rows.length > 1 ? "s" : ""}`}>
                {ranked.rows.map((p, idx) => {
                  // STRICT rate: use ONLY the rate for the selected rate type.
                  // Never fall back to distributor_price when rateKey is super_stockist
                  // — that would show the wrong price and mislead the user.
                  const rate = Number(p[rateKey] || 0);
                  const unpriced = rate <= 0;
                  const rateLabel = rateKey === "super_stockist_price" ? "SUPER STOCKIST" : "DISTRIBUTOR";
                  const isSelected = p.id === value;
                  const isRecent = !query && idx < ranked.showRecent;
                  return (
                    <CommandItem
                      key={p.id}
                      value={`${p.sku}__${p.name}__${p.category}__${p.id}`}
                      onSelect={() => { if (unpriced) return; onSelect(p); setOpen(false); }}
                      data-testid={`${testId}-option-${p.sku}`}
                      className={cn("cursor-pointer px-3 py-2 flex items-start gap-3", unpriced && "opacity-60 cursor-not-allowed")}
                      disabled={unpriced}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isRecent && <Clock className="h-3 w-3 text-amber-500 shrink-0" />}
                          <span className="text-[14px] font-bold text-slate-900 leading-snug">{p.name || p.sku}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 space-y-[2px]">
                          <div><span className="text-slate-400">SKU:</span> <span className="mono font-medium text-slate-700">{p.sku}</span>{p.category && p.category !== "Uncategorised" ? <> · <span className="text-slate-400">Category:</span> <span className="text-slate-700">{p.category}</span></> : null}</div>
                          {(p.pieces_per_case || p.case_weight_kg) ? (
                            <div className="text-slate-500">
                              {p.pieces_per_case ? `${p.pieces_per_case} pcs/box` : ""}
                              {p.pieces_per_case && p.case_weight_kg ? " • " : ""}
                              {p.case_weight_kg ? `${p.case_weight_kg} kg` : ""}
                            </div>
                          ) : null}
                          {p.scheme_text ? <div className="text-emerald-700 font-medium">Scheme: {p.scheme_text}</div> : null}
                        </div>
                      </div>
                      <div className="text-right shrink-0 min-w-[110px]">
                        {unpriced ? (
                          <>
                            <div className="text-[11px] font-semibold text-red-600 uppercase tracking-wide leading-tight">Not priced</div>
                            <div className="text-[9.5px] text-slate-500 uppercase tracking-wide mt-0.5">{rateLabel}</div>
                          </>
                        ) : (
                          <>
                            <div className="text-[14px] font-bold text-slate-900 tabular-nums leading-tight">{formatINR(rate)}</div>
                            <div className="text-[9.5px] text-slate-500 uppercase tracking-wide mt-0.5">{rateLabel} · per case</div>
                          </>
                        )}
                      </div>
                      <Check className={cn("h-4 w-4 text-emerald-600 shrink-0 mt-1", isSelected ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
